import "react-pdf/dist/Page/TextLayer.css";

import { useState, useRef, useEffect } from "react";
import { saveAs } from "file-saver";
import { DownloadIcon, FileXIcon } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";

import { EmptyState } from "@/components/EmptyState";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { cn } from "@/lib/utils";

// We follow the instructions from the react-pdf documentation:
// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#use-external-cdn
// https://github.com/wojtekmaj/react-pdf?tab=readme-ov-file#usage

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function DocumentPreview({
  centered,
  href,
  pageGap,
  pageWidthScale,
}: {
  centered?: boolean;
  href: string;
  pageGap?: number;
  pageWidthScale?: number;
}) {
  const [numPages, setNumPages] = useState<number>(1);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  // When width of this component changes due to split/reduce tabs button click
  // or activity side panel opening/closing, update width of document
  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    setWidth(element.clientWidth);

    const resizeObserver = new ResizeObserver(() => {
      // Set timeout for document resize to avoid flickering
      setTimeout(() => {
        setWidth(element.clientWidth);
      }, 300);
    });
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }): void => {
    setNumPages(numPages);
  };

  const unsupportedExtensions = [".doc", ".docx"];
  const unsupportedExtension = unsupportedExtensions.find((ext) =>
    href.endsWith(ext),
  );

  return (
    // allow the x to be clickable
    <div className="isolate" ref={containerRef}>
      {unsupportedExtension ? (
        <EmptyState
          description="To view this candidate's resume, download it below."
          handlePrimaryClick={() => {
            mixpanelUtils.trackButtonClick(
              "Download resume (unsupported format)",
              getScreenFromPathname(location.pathname),
            );
            const filename = href.substring(href.lastIndexOf("/") + 1);
            saveAs(href, filename);
          }}
          headerClassName="text-xl"
          Icon={FileXIcon}
          title="Resume not readable"
          PrimaryButtonIcon={DownloadIcon}
          primaryButtonName="Download resume"
          primaryButtonVariant="outline"
        />
      ) : (
        <Document
          className={cn("flex flex-col", centered && "items-center")}
          file={href}
          onLoadSuccess={onDocumentLoadSuccess}
        >
          <div
            className={cn("flex flex-col", centered && "items-center")}
            style={pageGap ? { gap: pageGap } : undefined}
          >
            {Array.from({ length: numPages }, (_, index) => (
              <Page
                className={cn(
                  !pageGap &&
                    "[&:not(:last-child)]:border-b [&:not(:last-child)]:border-slate-200",
                )}
                renderAnnotationLayer={false}
                renderTextLayer={true}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                width={width ? width * (pageWidthScale || 1) : undefined}
              />
            ))}
          </div>
        </Document>
      )}
    </div>
  );
}

export default DocumentPreview;
